import { useMutation } from '@tanstack/react-query';
import api, { TranscriptionResult } from '@/lib/api';

export interface TranscribeInput {
  fileId: string;
  language?: string;
}

/**
 * Transcription mutation. Returned as a `useMutation` rather than a
 * hand-rolled `useState` so the loading/error semantics line up with
 * the rest of the codebase (matches `docs/react-query-patterns.md`).
 *
 * Consumers commonly want `mutation.mutateAsync` so they can `await`
 * the result; the wrapper below preserves the `transcribing` /
 * `result` / `error` / `reset` names callers were already using to
 * minimize the diff at call sites.
 */
export function useTranscription() {
  const mutation = useMutation<TranscriptionResult, Error, TranscribeInput>({
    mutationFn: ({ fileId, language }) => api.transcribeAudio(fileId, { language }),
  });

  return {
    transcribe: async (fileId: string, language?: string): Promise<TranscriptionResult | null> => {
      try {
        return await mutation.mutateAsync({ fileId, language });
      } catch {
        return null;
      }
    },
    transcribing: mutation.isPending,
    result: mutation.data ?? null,
    error: mutation.error?.message ?? null,
    reset: mutation.reset,
  };
}
